import React, { useMemo } from 'react';
import { Typography, Grid, Box, Paper, Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Udalost } from '../api/JsdhNotifikaceService/generated';
import type { MapConfig, MapaLayout, PoplachTechnikaResponse, PosilovaNotifikaceLogResponse } from '../api/JsdhNotifikaceService/generated';
import type { UdalostRouteResponse } from '../api/JsdhNotifikaceService/generated/models/UdalostRoute';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { CasOdVyhlaseni } from './CasOdVyhlaseni';
import MapZoomWrapper from "./MapZoomWrapper";
import { ZpravyUTechniky } from './ZpravyUTechniky';
import { FitHeightBox } from './FitHeightBox';
import { formatAdresa } from '../utils/adresaUdalosti';

interface DetailPoplachusProps {
    aktivniPoplach: Udalost;
    showMapa?: boolean;
    mapConfig?: MapConfig;
    hideTechnikaJednotky?: boolean;
    mapaLayout?: MapaLayout;
    routeData?: UdalostRouteResponse | null;
    stationGps?: string;
    showCasVyhlaseni?: boolean;
    deviceToken?: string;
}

// Parsovat string techniky na části (jednotka, typ vozidla, volací znak)
export const parseTechnika = (technika: string): { jednotka: string; typ?: string; volaci?: string } => {
    // Tvary: "Jednotka: Typ - Volací" nebo (z povolit-techniku) "Typ - Volací" bez dvojtečky.
    // Dělí se podle POSLEDNÍHO ' - ', protože volací znak může obsahovat pomlčku (DA-L1Z).
    const colon = technika.indexOf(':');
    const jednotka = colon >= 0 ? technika.slice(0, colon).trim() : '';
    const zbytek = (colon >= 0 ? technika.slice(colon + 1) : technika).trim();
    const idx = zbytek.lastIndexOf(' - ');
    if (idx < 0) return { jednotka, typ: zbytek };
    return { jednotka, typ: zbytek.slice(0, idx).trim(), volaci: zbytek.slice(idx + 3).trim() };
};

const DetailPoplachu: React.FC<DetailPoplachusProps> = ({
    aktivniPoplach,
    showMapa = true,
    mapConfig,
    hideTechnikaJednotky = false,
    mapaLayout = 'misto',
    routeData,
    stationGps,
    showCasVyhlaseni = true,
    deviceToken,
}) => {

    // Query na detaily techniky s info o zdroji (KOPIS/VELITEL)
    const { data: technikaSources } = useQuery<PoplachTechnikaResponse[]>({
        queryKey: ['udalost-technika', aktivniPoplach.id, deviceToken],
        queryFn: () => JsdhNotifikaceService.getUdalostTechnika(aktivniPoplach.id, deviceToken),
        enabled: Boolean(aktivniPoplach.id),
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
        staleTime: 4000,
    });

    // Query na log posilových notifikací
    const { data: notifikaceLog } = useQuery<PosilovaNotifikaceLogResponse[]>({
        queryKey: ['udalost-notifikace-log', aktivniPoplach.id, deviceToken],
        queryFn: () => JsdhNotifikaceService.getNotifikaceLog(aktivniPoplach.id, deviceToken),
        enabled: Boolean(aktivniPoplach.id),
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
        staleTime: 4000,
    });

    // Map volací znak → source info pro badge na parsed technika kartách
    const sourceByVolaciZnak = useMemo(() => {
        const map = new Map<string, PoplachTechnikaResponse>();
        technikaSources?.forEach(t => map.set(t.volaciZnak, t));
        return map;
    }, [technikaSources]);

    // TEST DATA - smazat po otestování
    const testTechnikaJednotky = [
        "Kyjovice: CAS K25 LIAZ - HOP 001",
        "Pustá Polom: CAS 20 T815 Terno - HOP 002",
    ];
    const testDalsiTechnika = [
        "Bílovec: CAS 20 MAN - HOP 011",
        "Studénka: CAS 30 Tatra - HOP 012",
        "Klimkovice: CAS 24 Scania - HOP 013",
        "Ostrava: AZ 30 Iveco - HOP 014",
        "Opava: CAS 32 Tatra - HOP 015",
        "Hlučín: CAS 20 Mercedes - HOP 016",
        "Kravaře: CAS 24 Volvo - HOP 017",
        "Ludgeřovice: CAS 20 DAF - HOP 018",
        "Dolní Benešov: CAS 30 Renault - HOP 019",
    ];

    // VŽDY použít testovací data pro testování
    // const technikaJednotky = testTechnikaJednotky;
    // const dalsiTechnikaJednotek = testDalsiTechnika;
    const technikaJednotky = aktivniPoplach.technikaJednotky || [];
    const dalsiTechnikaJednotek = aktivniPoplach.dalsiTechnikaJednotek || [];
    const adresa = formatAdresa(aktivniPoplach);

    // Effective layout: zpetna kompatibilita s showMapa prop
    const hasRoute = !!routeData?.polyline && routeData.polyline.length > 0;
    let effectiveLayout: MapaLayout = mapaLayout !== 'misto'
        ? mapaLayout
        : (showMapa !== false ? 'misto' : 'off');
    // Fallback: pokud neni trasa, zobraz pouze mapu mista
    if ((effectiveLayout === 'misto-trasa' || effectiveLayout === 'trasa') && !hasRoute) {
        effectiveLayout = 'misto';
    }

    const hasGps = !!aktivniPoplach.gps;

    // Determine grid sizes based on layout
    const showMistoMap = hasGps && (effectiveLayout === 'misto' || effectiveLayout === 'misto-trasa');
    const showTrasaMap = hasGps && hasRoute && (effectiveLayout === 'trasa' || effectiveLayout === 'misto-trasa');

    let infoMd: number;
    let mapMd: number;
    if (effectiveLayout === 'off' || !hasGps) {
        infoMd = 12;
        mapMd = 0;
    } else if (effectiveLayout === 'misto-trasa') {
        infoMd = 4;
        mapMd = 8; // split between 2 maps (4+4)
    } else {
        infoMd = 5;
        mapMd = 7;
    }

    const routeDistanceLabel = routeData
        ? `${routeData.distanceKm?.toFixed(1).replace('.', ',')} km - ${Math.round(routeData.durationMin ?? 0)} min`
        : undefined;

    // Zprávy u techniky: pod mapou (širší sloupec s pevným stropem výšky), jinak v informačním sloupci.
    // Dostanou jen zbylé místo a samy zmenší písmo; nesmí vytlačit techniku a odpovědi pod okraj monitoru.
    const zpravyPodMapou = showMistoMap || (effectiveLayout === 'trasa' && hasGps) || (effectiveLayout === 'misto-trasa' && hasGps);
    const zpravy = (
        <ZpravyUTechniky
            posilovaTechnikaTexty={aktivniPoplach.posilovaTechnikaTexty}
            posledniZprava={notifikaceLog && notifikaceLog.length > 0 ? notifikaceLog[0] : undefined}
            technikaSources={technikaSources}
            maxHeight={zpravyPodMapou ? '40%' : undefined}
        />
    );

    const detailRows: { label: string; value: string }[] = [
        { label: 'Objekt', value: aktivniPoplach.objekt },
        { label: 'Popis', value: aktivniPoplach.popis },
        { label: 'Upřesnění', value: aktivniPoplach.upresneni },
        { label: 'Adresa', value: adresa },
        { label: 'Trasa', value: routeDistanceLabel && (effectiveLayout === 'trasa' || effectiveLayout === 'misto-trasa') ? routeDistanceLabel : '' },
    ].filter((r): r is { label: string; value: string } => !!r.value);

    const InfoPanel = (
        <Grid data-testid="info-sloupec" size={{ xs: 12, md: infoMd }} sx={{
            display: 'flex', flexDirection: 'column', minHeight: 0,
            // Strop výšky = výška mapy: bez něj informační sloupec přerostl Layout (overflow:auto)
            // a technika/odpovědi skončily pod okrajem monitoru, kam kiosek nedoscrolluje.
            // Platí i bez mapy (poplach bez GPS, mapa vypnutá): jinak detaily a zprávy zabraly
            // celou výšku a karty techniky a odpovědí v technika-ucast se zmáčkly na nulu.
            ...(mapMd > 0
                ? { maxHeight: { md: 'var(--dashboard-map-height)' }, overflow: { md: 'hidden' } }
                : { maxHeight: 'var(--dashboard-map-height)', overflow: 'hidden' }),
        }}>
            {aktivniPoplach.udalost && (
                <Box pb={{ xs: 1, md: 1 }}>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', fontSize: 'var(--dashboard-event-size)', lineHeight: 1.2 }}>
                        {aktivniPoplach.udalost}
                    </Typography>
                </Box>
            )}
            {/* Detaily dostanou jen místo, které zbude po technice: písmo se zmenší (nejvýš na 16 px),
                aby technika a další jednotky pod nimi zůstaly vždy celé vidět (sloupec má strop výšky mapy). */}
            {detailRows.length > 0 && (
                <FitHeightBox data-testid="detail-info" maxFont="var(--dashboard-info-size)" fitKey={detailRows.map(r => r.value).join('|')}
                    /* Bez mapy sdílí sloupec se zprávami: detaily (velké písmo) se zmenšují první,
                       aby zprávy a poznámky velitele zůstaly celé, dokud to jde. */
                    sx={{ flex: zpravyPodMapou ? '0 1 auto' : '0 100 auto', bgcolor: 'background.paper' }}>
                    {detailRows.map(r => (
                        <Box key={r.label} sx={{ mb: 'var(--dashboard-gap)' }}>
                            <Typography sx={{ color: 'text.secondary', fontSize: '1rem', lineHeight: 1 }}>
                                {r.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1em', fontWeight: 500, lineHeight: 1.2, wordBreak: 'break-word' }}>
                                {r.value}
                            </Typography>
                        </Box>
                    ))}
                </FitHeightBox>
            )}

            {/* Technika */}
            <Box data-testid="info-technika" sx={{ mt: 1, display: 'flex', flexDirection: 'column', flex: '0 0 auto' }}>
                {!hideTechnikaJednotky && (
                    <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary", fontSize: '1rem', lineHeight: 1.2, mb: 0.5 }}>
                            Technika
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {technikaJednotky?.length > 0 ? (
                                technikaJednotky.map((t, index) => {
                                    const { jednotka, typ, volaci } = parseTechnika(t);
                                    const source = volaci ? sourceByVolaciZnak.get(volaci) : undefined;
                                    // Jeden řádek na vůz (bez názvu vlastní jednotky): ve sloupci se strop výšky
                                    // musí vejít celá technika i další jednotky, písmo min. 16 px.
                                    return (
                                        <Paper key={`technika-${index}`} data-testid="info-technika-chip" elevation={1} sx={{ px: 0.75, py: 0.25, display: 'flex', flexWrap: 'wrap', columnGap: 0.75, alignItems: 'baseline', borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                            <Typography sx={{ fontSize: '1.1rem', color: 'primary.main', fontWeight: 700, lineHeight: 1.25, whiteSpace: 'nowrap' }}>
                                                {volaci || jednotka}
                                            </Typography>
                                            <Typography sx={{ fontSize: '1rem', fontWeight: 500, lineHeight: 1.25 }}>
                                                {typ}
                                            </Typography>
                                            {source && source.zdroj === 'VELITEL' && (
                                                <Chip
                                                    size="small"
                                                    label={source.potvrzenoKopis ? 'Velitel ✓ KOPIS' : 'Velitel'}
                                                    sx={{
                                                        height: 'auto',
                                                        fontWeight: 700,
                                                        fontSize: '1rem',
                                                        color: '#fff',
                                                        bgcolor: source.potvrzenoKopis ? 'success.main' : 'warning.main',
                                                        '& .MuiChip-label': { px: 0.5 },
                                                    }}
                                                />
                                            )}
                                        </Paper>
                                    );
                                })
                            ) : (
                                <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.disabled", fontSize: '1rem' }}>
                                    Žádná technika
                                </Typography>
                            )}
                        </Box>
                    </>
                )}

                {dalsiTechnikaJednotek?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary", fontSize: '1rem', lineHeight: 1.2, mb: 0.5 }}>
                            Další jednotky
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                            {dalsiTechnikaJednotek.slice(0, 4).map((t, index) => {
                                const { jednotka, typ, volaci } = parseTechnika(t);
                                return (
                                    <Paper key={`dalsi-technika-${index}`} data-testid="info-dalsi-chip" elevation={1} sx={{ px: 0.75, py: 0.25, display: 'flex', flexWrap: 'wrap', columnGap: 0.75, alignItems: 'baseline', borderLeft: '3px solid', borderColor: 'grey.400' }}>
                                        {jednotka && (
                                            <Typography sx={{ fontSize: '1rem', color: 'text.secondary', lineHeight: 1.25 }}>
                                                {jednotka}:
                                            </Typography>
                                        )}
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 500, lineHeight: 1.25 }}>
                                            {typ}
                                        </Typography>
                                        <Typography sx={{ fontSize: '1rem', color: 'text.secondary', fontWeight: 700, lineHeight: 1.25, whiteSpace: 'nowrap' }}>
                                            {volaci}
                                        </Typography>
                                    </Paper>
                                );
                            })}
                            {dalsiTechnikaJednotek.length > 4 && (
                                <Typography sx={{ color: "text.secondary", fontSize: '1rem', fontStyle: 'italic' }}>
                                    + {dalsiTechnikaJednotek.length - 4} dalších
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Bez mapy jsou zprávy přímo ve sloupci (ne v nesmršťovaném bloku techniky):
                dostanou jen zbylé místo pod stropem sloupce a zmenší písmo. */}
            {!zpravyPodMapou && zpravy}
        </Grid>
    );

    return (
        <Box sx={{ p: 1, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
            <Grid container spacing={0}>
                {/* Nadpis + čas */}
                <Grid size={{ xs: 12 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0} mt={0.5} flexWrap="wrap" gap={1}>
                        {aktivniPoplach.pred && (
                            <Typography variant="h1" sx={{ fontWeight: 'bold', fontSize: 'var(--dashboard-title-size)', lineHeight: 1.1 }}>
                                {aktivniPoplach.pred.toLocaleUpperCase()}
                            </Typography>
                        )}
                        {showCasVyhlaseni && aktivniPoplach?.casVyhlaseni && (
                            <CasOdVyhlaseni casVyhlaseni={aktivniPoplach.casVyhlaseni} />
                        )}
                    </Box>
                </Grid>

                {/* Obsah - informace + mapa vedle sebe */}
                <Grid size={{ xs: 12 }}>
                    <Grid container spacing={0} sx={{ alignItems: 'stretch' }}>
                        {/* Info panel */}
                        {InfoPanel}

                        {/* Layout: misto — jedna mapa místa */}
                        {effectiveLayout === 'misto' && showMistoMap && (
                            <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex', flexDirection: 'column', height: 'var(--dashboard-map-height)', minHeight: 150 }}>
                                <Box sx={{ flex: 1, minHeight: 0, mt: { xs: 2, md: 0 } }}>
                                    <MapZoomWrapper
                                        gps={aktivniPoplach.gps}
                                        hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                                        showHyndrant={aktivniPoplach.pred === "POŽÁR"}
                                        height="100%"
                                        mapConfig={mapConfig}
                                    />
                                </Box>
                                {zpravy}
                            </Grid>
                        )}

                        {/* Layout: trasa — jedna mapa fitted na trasu */}
                        {effectiveLayout === 'trasa' && hasGps && (
                            <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex', flexDirection: 'column', height: 'var(--dashboard-map-height)', minHeight: 150 }}>
                                <Box sx={{ flex: 1, minHeight: 0, mt: { xs: 2, md: 0 } }}>
                                    <MapZoomWrapper
                                        gps={aktivniPoplach.gps}
                                        hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                                        showHyndrant={false}
                                        height="100%"
                                        mapConfig={mapConfig}
                                        routeCoordinates={routeData?.polyline}
                                        stationGps={stationGps}
                                        fitToBounds={hasRoute}
                                    />
                                </Box>
                                {zpravy}
                            </Grid>
                        )}

                        {/* Layout: misto-trasa — dve mapy vedle sebe */}
                        {effectiveLayout === 'misto-trasa' && hasGps && (
                            <>
                                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', height: 'var(--dashboard-map-height)', minHeight: 150 }}>
                                    <Box sx={{ flex: 1, minHeight: 0, mt: { xs: 2, md: 0 } }}>
                                        <MapZoomWrapper
                                            gps={aktivniPoplach.gps}
                                            hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                                            showHyndrant={aktivniPoplach.pred === "POŽÁR"}
                                            height="100%"
                                            mapConfig={mapConfig}
                                        />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', height: 'var(--dashboard-map-height)', minHeight: 150, pl: '2px' }}>
                                    <Box sx={{ flex: 1, minHeight: 0, mt: { xs: 2, md: 0 } }}>
                                        <MapZoomWrapper
                                            gps={aktivniPoplach.gps}
                                            hasCisloPopisne={!!aktivniPoplach.cisloPopisne}
                                            showHyndrant={false}
                                            height="100%"
                                            routeCoordinates={routeData?.polyline}
                                            stationGps={stationGps}
                                            fitToBounds={hasRoute}
                                        />
                                    </Box>
                                    {zpravy}
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default React.memo(DetailPoplachu);
